import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth } from '../../../lib/firebase';
import { t as uiText } from '../../../i18n';
import { useEmailVerification } from '../EmailVerificationContext';
import { readCallableError, requestEmailChange } from '../api';

function changeErrorMessage(code: string, retryAfterSec?: number): string {
  switch (code) {
    case 'INVALID_EMAIL':
      return uiText('The email address format is invalid.');
    case 'SAME_EMAIL':
      return uiText('This is the same as your current email address.');
    case 'EMAIL_IN_USE':
      return uiText('This email address is already in use.');
    case 'REAUTH_REQUIRED':
      return uiText('Re-authentication is required. Please enter your password.');
    case 'RATE_LIMITED':
      return retryAfterSec && retryAfterSec <= 3600
        ? uiText('Please wait a moment before trying again.')
        : uiText('You have reached the daily limit for email changes. Please try again later.');
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
      return uiText('The password is incorrect.');
    case 'auth/too-many-requests':
      return uiText('Too many attempts. Please wait a while and try again.');
    default:
      return uiText('Could not send the confirmation email. Please try again later.');
  }
}

interface Props {
  className?: string;
  label?: string;
}

const ChangeEmailDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { email, pendingEmail } = useEmailVerification();
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const user = auth?.currentUser;
    if (!user || !user.email) return;
    setBusy(true);
    setError(null);
    try {
      // 再認証 → 最新の auth_time を持つトークンをサーバーへ
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
      await user.getIdToken(true);
      const r = await requestEmailChange(newEmail.trim());
      setSentTo(r.newEmail);
      setPassword('');
    } catch (err) {
      const info = readCallableError(err);
      setError(changeErrorMessage(info.code, info.retryAfterSec));
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="ev-overlay ev-overlay-top" role="presentation">
      <div className="ev-dialog" role="dialog" aria-modal="true" aria-labelledby="ev-change-title">
        <h2 id="ev-change-title" className="ev-title">
          {uiText('Change email address')}
        </h2>
        {sentTo ? (
          <>
            <p role="status">
              {uiText('We sent a confirmation email to {{email}}. Open the link in that email to complete the change.', {
                email: sentTo,
              })}
            </p>
            <p className="ev-note">
              {uiText('Until the change is completed, your current email address stays registered. After the change, you may need to sign in again with the new address.')}
            </p>
            <div className="ev-actions">
              <button type="button" className="ev-btn ev-btn-primary" onClick={onClose}>
                {uiText('Close')}
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={submit}>
            <p className="ev-note">
              {uiText('Current email address')}: <strong>{email}</strong>
            </p>
            {pendingEmail && (
              <p className="ev-note">
                {uiText('A change to {{email}} is waiting for confirmation.', { email: pendingEmail })}
              </p>
            )}
            <label className="ev-label" htmlFor="ev-new-email">
              {uiText('New email address')}
            </label>
            <input
              id="ev-new-email"
              className="ev-input"
              type="email"
              autoComplete="email"
              required
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
            />
            <label className="ev-label" htmlFor="ev-current-password">
              {uiText('Current password')}
            </label>
            <input
              id="ev-current-password"
              className="ev-input"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {error && (
              <div className="ev-msg ev-msg-error" role="alert">
                {error}
              </div>
            )}
            <div className="ev-actions">
              <button type="submit" className="ev-btn ev-btn-primary" disabled={busy}>
                {uiText('Send confirmation email')}
              </button>
              <button type="button" className="ev-btn" onClick={onClose} disabled={busy}>
                {uiText('Cancel')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
};

/** 「メールアドレスを変更する」ボタン + ダイアログ */
const ChangeEmailButton: React.FC<Props> = ({ className, label }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={`ev-btn ${className ?? ''}`} onClick={() => setOpen(true)}>
        {label ?? uiText('Change email address')}
      </button>
      {open && <ChangeEmailDialog onClose={() => setOpen(false)} />}
    </>
  );
};

export default ChangeEmailButton;
